/**
 * Cart Context
 * 
 * Manages cart state across the layout app.
 * Provides show/hide cart drawer and cart token management.
 */

'use client';

import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { getCookie, deleteCookie, CLEAR_CART_COOKIE, CLEAR_CART_ORDER_COOKIE } from '@/lib/utils/cookies';
import { trackPurchase } from '@/lib/analytics';

const CART_TOKEN_STORAGE_KEY = 'cart_token';

interface CartContextType {
  cartToken: string | null;
  setCartToken: (token: string | null) => void;
  clearCart: () => void;
  isCartOpen: boolean;
  showCart: () => void;
  hideCart: () => void;
  toggleCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartToken, setCartTokenState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(CART_TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Check for te_clear_cart cookie on mount.
  // The external checkout app sets this cookie on the parent domain after
  // order completion. When the user returns (page reload, bfcache, or
  // direct navigation), we clear the cart and fire the purchase event.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const clearCartCookie = getCookie(CLEAR_CART_COOKIE);
    if (clearCartCookie !== 'true') return;

    const orderId = getCookie(CLEAR_CART_ORDER_COOKIE);

    setCartTokenState(null);
    localStorage.removeItem(CART_TOKEN_STORAGE_KEY);

    if (orderId) {
      trackPurchase({ orderId });
    }

    deleteCookie(CLEAR_CART_COOKIE);
    deleteCookie(CLEAR_CART_ORDER_COOKIE);
  }, []);

  // Persist cart token to localStorage
  const setCartToken = (token: string | null) => {
    setCartTokenState(token);
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem(CART_TOKEN_STORAGE_KEY, token);
      } else {
        localStorage.removeItem(CART_TOKEN_STORAGE_KEY);
      }
    }
  };

  // Clear cart - removes token from state and localStorage
  const clearCart = () => {
    setCartTokenState(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(CART_TOKEN_STORAGE_KEY);
    }
  };

  const showCart = () => {
    setIsCartOpen(true);
  };

  const hideCart = () => {
    setIsCartOpen(false);
  };

  const toggleCart = () => {
    setIsCartOpen(prev => !prev);
  };

  return (
    <CartContext.Provider
      value={{
        cartToken,
        setCartToken,
        clearCart,
        isCartOpen,
        showCart,
        hideCart,
        toggleCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCartContext() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCartContext must be used within a CartProvider');
  }
  return context;
}
